import {
  Badge,
  BlockStack,
  Box,
  Button,
  Checkbox,
  Grid,
  Icon,
  InlineStack,
  LegacyCard,
  Modal,
  ProgressBar,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  TextContainer,
} from "@shopify/polaris";
import {
  CheckIcon,
  StarFilledIcon,
  StarIcon,
  XSmallIcon,
  ConfettiIcon,
  CalendarTimeIcon,
} from "@shopify/polaris-icons";
import {
  useCreateSubscriptionMutation,
  useGetSubscriptionStatusQuery,
} from "../store/store/slices/BillingRTS";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../store/store/slices/AlertSlice";
import formatDate from "../hooks/useDate";
import CancelSubscription from "../components/atoms/plans/CancelSubscription";
import { useSearchParams } from "react-router-dom";
import { FREE_PLAN_RESTRICTIONS } from "../utils/enums";
import dayjs from "dayjs";
interface FEATURE {
  title: string;
  available: boolean;
}
interface PLAN {
  id: string;
  title: string;
  price: number;
  currencySign: string;
  isFree: boolean;
  durationAsPer: string;
  subscribed: boolean;
  isPopular: boolean;
  mostlyUsed: boolean;
  features: FEATURE[];
}
const APP_PLANS: PLAN[] = [
  {
    id: "FREE",
    title: "Basic plan",
    isFree: true,
    currencySign: "$",
    durationAsPer: "Month",
    isPopular: false,
    subscribed: false,
    mostlyUsed: false,
    features: [
      { title: "Upto 5000 order bookings per month", available: true },
      { title: "Automatic status update", available: false },
      { title: "Maximum 10 booking accounts", available: true },
      { title: "Maximum 10 user accounts", available: true },
      { title: "Orders data export (csv)", available: false },
      { title: "Two factor protection", available: false },
      { title: "Partial settings", available: true },
    ],
    price: 0,
  },
  {
    id: "MONTHLY",
    title: "Monthly plan",
    currencySign: "$",
    durationAsPer: "Month",
    isPopular: true,
    isFree: false,
    subscribed: false,
    mostlyUsed: false,
    features: [
      { title: "Unlimited orders booking", available: true },
      { title: "Automatic status update", available: true },
      { title: "Unlimited booking accounts", available: true },
      { title: "Unlimited user accounts", available: true },
      { title: "Orders data export (csv)", available: true },
      { title: "Two factor protection", available: true },
      { title: "All settings", available: true },
    ],
    price: 10,
  },
  {
    id: "YEARLY",
    title: "Yearly plan",
    currencySign: "$",
    durationAsPer: "Year",
    isPopular: false,
    isFree: false,
    subscribed: false,
    mostlyUsed: true,
    features: [
      { title: "Unlimited orders booking", available: true },
      { title: "Automatic status update", available: true },
      { title: "Unlimited booking accounts", available: true },
      { title: "Unlimited user accounts", available: true },
      { title: "Orders data export (csv)", available: true },
      { title: "Two factor protection", available: true },
      { title: "All settings", available: true },
    ],
    price: 99.99,
  },
];
interface CONFIRMATION_MODAL_TYPE {
  status: boolean;
  id: string;
}
interface ACTIVATE_FLAG_TYPE {
  users: boolean;
  accounts: boolean;
}
const Plans = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [activateFlag, setActivateFlag] = useState<ACTIVATE_FLAG_TYPE>({
    users: false,
    accounts: false,
  });
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] =
    useState<CONFIRMATION_MODAL_TYPE>({ status: false, id: "" });
  const [selectedId, setSelectedId] = useState<string>("");
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const {
    data,
    isLoading: gettingSubscriptionStatus,
    refetch,
    isFetching,
  } = useGetSubscriptionStatusQuery();
  const [createSubscription, { isLoading, isError, isSuccess, error }] =
    useCreateSubscriptionMutation();
  useEffect(() => {
    if (isError) {
      dispatch(
        showToast({
          error: true,
          message: error?.data?.errors[0]?.msg || "Something went wrong",
          visible: true,
        })
      );
    }
  }, [isLoading, isError, isSuccess]);
  useEffect(() => {
    setFeedbackModal(
      () =>
        searchParams.get("success") === "true" ||
        searchParams.get("success") === "false"
    );
  }, []);
  const onSubscribePlanHandler = async (planId: string) => {
    setSelectedId(planId);
    const postSubscriptionResponse = await createSubscription({
      plan: planId,
      activateUsers: activateFlag.users,
      activateTracking: activateFlag.accounts,
    }).unwrap();
    if (postSubscriptionResponse?.confirmationUrl) {
      window.location.href = postSubscriptionResponse?.confirmationUrl;
    }
  };
  const openModal = (id: string) => {
    setOpenConfirmModal({ status: true, id });
  };
  const closeModal = () => {
    setOpenConfirmModal({ status: false, id: "" });
  };
  const closeFeedbackModal = () => {
    setFeedbackModal(false);
  };

  // JSX CODE

  const planProgressPercentage = Number(
    ((data?.orders / FREE_PLAN_RESTRICTIONS.orders) * 100).toFixed(2)
  );

  return (
    <div
      style={{
        marginTop: "2rem auto",
        width: "max-content",
        margin: "auto",
      }}
    >
      <div style={{ margin: "2rem 0rem" }}>
        <Text as="h1" fontWeight="bold" variant="headingLg">
          Pricing
        </Text>
      </div>
      <Grid columns={{ sm: 1, md: 2, lg: 3 }}>
        {!gettingSubscriptionStatus &&
          !isFetching &&
          APP_PLANS.map((plan: PLAN, index: number) => (
            <div
              key={index}
              style={{
                position: "relative",
                height: "max-content",
                border: `${
                  (data?.subscribed && data?.plan === plan.id) ||
                  (!data?.subscribed && !data?.plan && plan.id === "FREE")
                    ? "3px solid #047b5d"
                    : "none"
                }`,
                borderRadius: "13px",
                transform: `${
                  index === 1 ? "translateY(0px)" : "translateY(0px)"
                }`,
              }}
            >
              <Box
                borderWidth="0165"
                borderRadius="300"
                width="330px"
                borderColor="border"
                padding={"400"}
                background="bg-fill"
                // minHeight="100%"
              >
                <BlockStack gap={"500"}>
                  {plan.isPopular && (
                    <div>
                      <Badge icon={StarFilledIcon} tone="magic">
                        Recommended
                      </Badge>
                    </div>
                  )}
                  {plan.mostlyUsed && (
                    <div>
                      <Badge icon={ConfettiIcon} tone="success">
                        Mostly used
                      </Badge>
                    </div>
                  )}
                  {plan.id === "EXTEND" && (
                    <div>
                      <Badge tone="success-strong" icon={StarIcon}>
                        Extendable
                      </Badge>
                    </div>
                  )}

                  <Text as="p">{plan.title}</Text>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: ".5rem",
                    }}
                  >
                    {plan.isFree ? (
                      <>
                        <Text as="span" variant="headingXl">
                          Free
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text as="span" variant="headingXl">
                          {plan.price} {plan.currencySign}
                        </Text>
                        <span style={{ textTransform: "lowercase" }}>
                          /{plan.durationAsPer}
                        </span>
                      </>
                    )}
                  </div>

                  <BlockStack gap={"300"}>
                    {plan.features.map((feature: FEATURE, index: number) => (
                      <InlineStack gap={"300"} key={index}>
                        {feature.available ? (
                          <div
                            style={{
                              height: "20px",
                              width: "20px",
                              borderRadius: "100%",
                              background: "rgb(225 255 217)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span style={{ scale: ".9" }}>
                              <Icon source={CheckIcon} tone="success"></Icon>
                            </span>
                          </div>
                        ) : (
                          <div
                            style={{
                              height: "20px",
                              width: "20px",
                              borderRadius: "100%",
                              background: "#f5c7c7",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span style={{ scale: "1" }}>
                              <Icon source={XSmallIcon} tone="critical"></Icon>
                            </span>
                          </div>
                        )}
                        <Text as="p" variant="bodyMd">
                          {feature.title}
                        </Text>
                      </InlineStack>
                    ))}
                  </BlockStack>
                  {data?.plan === plan.id && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                        gap: ".3rem",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          padding: ".2rem",
                          borderRadius: "100%",
                          background: "#ebebeb",
                        }}
                      >
                        <Icon source={CalendarTimeIcon} tone="interactive" />
                      </span>{" "}
                      Expires at: {formatDate(data?.expiry)}
                    </div>
                  )}
                  {!data?.subscribed && plan.id === "FREE" && (
                    <BlockStack gap={"200"}>
                      {planProgressPercentage <= 100 ? (
                        <InlineStack gap={"100"}>
                          <p>
                            Booked {data?.orders} orders this month{" "}
                            {dayjs().format("MMMM")}
                          </p>
                        </InlineStack>
                      ) : (
                        <p>Max limit reached</p>
                      )}
                      <ProgressBar
                        animated
                        progress={planProgressPercentage}
                        tone="primary"
                        size="medium"
                      />
                    </BlockStack>
                  )}
                  <div>
                    <Button
                      fullWidth
                      variant={theme === "dark" ? "secondary" : "primary"}
                      disabled={
                        isLoading ||
                        (data?.subscribed && data?.plan === plan.id) ||
                        selectedId === plan.id ||
                        plan.id === "FREE"
                      }
                      loading={selectedId === plan.id && isLoading}
                      onClick={() => openModal(plan.id)}
                    >
                      {plan.id === "FREE"
                        ? "Auto subscribed"
                        : data?.subscribed && data?.plan === plan.id
                        ? "Subscribed"
                        : "Select plan"}
                    </Button>
                    {plan.id !== "FREE" && (
                      <CancelSubscription
                        refetchSubscription={refetch}
                        name={plan.id}
                        id={data?.plan === plan.id ? data.planId : ""}
                      />
                    )}
                  </div>
                </BlockStack>
              </Box>
            </div>
          ))}
        {(gettingSubscriptionStatus || isFetching) &&
          APP_PLANS.map((plan: PLAN, index: number) => (
            <div style={{ width: "330px" }} key={index}>
              <LegacyCard subdued>
                <LegacyCard.Section>
                  <TextContainer>
                    <SkeletonDisplayText size="small" />
                    <SkeletonDisplayText size="small" />
                    <div style={{ maxWidth: "75%" }}>
                      <SkeletonBodyText lines={5} />
                    </div>
                    <div style={{ maxWidth: "75%" }}>
                      <SkeletonBodyText lines={5} />
                    </div>
                    {index === 1 && <SkeletonBodyText lines={1} />}
                  </TextContainer>
                </LegacyCard.Section>
                <LegacyCard.Section>
                  <SkeletonDisplayText size="small" maxWidth="100%" />
                </LegacyCard.Section>
              </LegacyCard>
            </div>
          ))}
      </Grid>
      <Modal
        onClose={closeFeedbackModal}
        open={feedbackModal}
        title={
          <Text
            as="span"
            tone={
              searchParams.get("success") === "false" ? "critical" : undefined
            }
          >
            Subscription status
          </Text>
        }
        primaryAction={{
          content: "Okay",
          onAction: () => {
            closeFeedbackModal();
          },
        }}
      >
        {searchParams.get("success") === "true" && (
          <Modal.Section>
            <p>You have been successfully subscribed to our PAID plan</p>
            <Text as="h1" fontWeight="bold">
              __ Happy booking. 💖
            </Text>
          </Modal.Section>
        )}
        {searchParams.get("success") === "false" && (
          <Modal.Section>
            <p>Something went wrong. Please try again later </p>
          </Modal.Section>
        )}
      </Modal>
      <>
        <Modal
          onClose={closeModal}
          open={openConfirmModal?.status}
          title="Are you sure?"
          primaryAction={{
            content: "Yes, proceed!",
            onAction: () => {
              onSubscribePlanHandler(openConfirmModal?.id);
              closeModal();
            }
            
          }}
          secondaryActions={[{ content: "Cancel", onAction: closeModal }]}
        >
          <Modal.Section>
            <BlockStack gap={"200"}>
              <p>
                You are going to subscribe Bookease {openConfirmModal?.id} plan.
              </p>
              {data?.subscriber && (
                <BlockStack gap={"200"}>
                  <Checkbox
                    label="Activate all user accounts"
                    checked={activateFlag.users}
                    onChange={(status) =>
                      setActivateFlag((prev) => {
                        return { accounts: prev.accounts, users: status };
                      })
                    }
                  />
                  <Checkbox
                    label="Activate all tracking accounts"
                    checked={activateFlag.accounts}
                    onChange={(status) =>
                      setActivateFlag((prev) => {
                        return { users: prev.users, accounts: status };
                      })
                    }
                  />
                </BlockStack>
              )}
              <p>Do you really want to proceed?</p>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </>
    </div>
  );
};

export default Plans;
